import { useState } from "react";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";

export interface TestimonialItem {
  id: string;
  author_en: string;
  author_tr: string;
  role_en: string;
  role_tr: string;
  company_en: string;
  company_tr: string;
  quote_en: string;
  quote_tr: string;
  avatar_url?: string;
  sort_order: number;
  links?: string | any[];
}

export function safeParseLinks(links: any): any[] {
  if (!links) return [];
  if (Array.isArray(links)) return links;
  try {
    const parsed = typeof links === "string" ? JSON.parse(links) : links;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useTestimonialsDraft(
  editingTestimonial: Partial<TestimonialItem> | null,
  setEditingTestimonial: React.Dispatch<React.SetStateAction<Partial<TestimonialItem> | null>>,
  testimonials: TestimonialItem[],
  activeDraft: any,
  setActiveDraft: React.Dispatch<React.SetStateAction<any>>
) {
  const handleSaveDraft = async () => {
    if (!editingTestimonial) return;
    try {
      const draftPayload = {
        ...editingTestimonial,
        links: typeof editingTestimonial.links === "string" ? editingTestimonial.links : JSON.stringify(editingTestimonial.links || [])
      };
      await adminFetch("/api/admin/drafts", {
        operation: "save",
        item_type: "testimonial",
        item_id: editingTestimonial.id,
        draft_data: draftPayload,
      });
      setActiveDraft({
        ...draftPayload,
        links: safeParseLinks(draftPayload.links)
      });
      toast.success("Testimonial draft saved to staging successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to save draft");
    }
  };

  const handleDiscardDraft = async () => {
    if (!editingTestimonial) return;
    if (!confirm("Are you sure you want to discard this staged draft?")) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "testimonial",
        item_id: editingTestimonial.id,
      });
      setActiveDraft(null);
      const live = testimonials.find((t) => t.id === editingTestimonial.id);
      setEditingTestimonial(live || null);
      toast.success("Staged draft discarded successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to discard draft");
    }
  };

  return { handleSaveDraft, handleDiscardDraft };
}

async function swapTestimonialOrder(tA: TestimonialItem, tB: TestimonialItem, onRefresh: () => void) {
  try {
    await adminFetch("/api/admin/testimonials", { ...tA, sort_order: tB.sort_order });
    await adminFetch("/api/admin/testimonials", { ...tB, sort_order: tA.sort_order });
    onRefresh();
  } catch (err: any) {
    toast.error(err.message || "Failed to reorder items");
  }
}

const createDefaultTestimonial = (length: number): Partial<TestimonialItem> => ({
  id: `testimonial-${Date.now()}`,
  author_en: "",
  author_tr: "",
  role_en: "",
  role_tr: "",
  company_en: "",
  company_tr: "",
  quote_en: "",
  quote_tr: "",
  avatar_url: "",
  sort_order: length,
  links: [],
});

export function useTestimonialsManager(testimonials: TestimonialItem[], onRefresh: () => void) {
  const [editingTestimonial, setEditingTestimonial] = useState<Partial<TestimonialItem> | null>(null);
  const [activeDraft, setActiveDraft] = useState<any>(null);

  const { handleSaveDraft, handleDiscardDraft } = useTestimonialsDraft(
    editingTestimonial,
    setEditingTestimonial,
    testimonials,
    activeDraft,
    setActiveDraft
  );

  const startAdd = () => {
    setActiveDraft(null);
    setEditingTestimonial(createDefaultTestimonial(testimonials.length));
  };

  const handleEdit = (testimonial: TestimonialItem) => {
    const parsedTestimonial = {
      ...testimonial,
      links: safeParseLinks(testimonial.links)
    };
    setEditingTestimonial(parsedTestimonial);
    adminFetch(`/api/admin/drafts?item_type=testimonial&item_id=${testimonial.id}`)
      .then((res) => {
        if (res?.success && res.draft) {
          const draftParsed = {
            ...res.draft,
            links: safeParseLinks(res.draft.links)
          };
          setActiveDraft(draftParsed);
          setEditingTestimonial(draftParsed);
          toast.info("Unpublished staged draft loaded for editing.");
        } else {
          setActiveDraft(null);
        }
      })
      .catch(() => setActiveDraft(null));
  };

  const handleSave = async () => {
    if (!editingTestimonial?.author_en || !editingTestimonial?.quote_en) {
      toast.error("Author Name and Quote (English) are required!");
      return;
    }
    try {
      const payload = {
        ...editingTestimonial,
        links: typeof editingTestimonial.links === "string" ? JSON.parse(editingTestimonial.links) : (editingTestimonial.links || [])
      };
      await adminFetch("/api/admin/drafts", {
        operation: "promote",
        item_type: "testimonial",
        item_id: editingTestimonial.id,
        draft_data: payload,
      });
      toast.success("Testimonial saved successfully!");
      setEditingTestimonial(null);
      setActiveDraft(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save testimonial");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this testimonial?")) return;
    try {
      await adminFetch("/api/admin/testimonials", { operation: "delete", id });
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "testimonial",
        item_id: id,
      });
      toast.success("Testimonial deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete testimonial");
    }
  };

  const handleMove = async (index: number, direction: "up" | "down") => {
    const sorted = [...testimonials].sort((a, b) => a.sort_order - b.sort_order);
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const tA = sorted[index];
    const tB = sorted[swapIndex];
    if (tA && tB) {
      await swapTestimonialOrder(tA, tB, onRefresh);
    }
  };

  return {
    editingTestimonial,
    setEditingTestimonial,
    activeDraft,
    setActiveDraft,
    startAdd,
    handleEdit,
    handleSaveDraft,
    handleDiscardDraft,
    handleSave,
    handleDelete,
    handleMove
  };
}

export default useTestimonialsManager;
